import { AssetDownload, AssetDownloadProps } from "~/components/AssetDownload";
import { AuthorBio, AuthorBioProps } from "~/components/AuthorBio";
import {
  AuthorBylineSimple,
  AuthorBylineSimpleProps,
} from "~/components/AuthorBylineSimple";
import { BuildHtml } from "~/components/BuildHtml";
import { ContentPromo, ContentPromoProps } from "~/components/ContentPromo";
import { Divider } from "~/components/Divider";
import { EvervizChart, EvervizChartProps } from "~/components/EvervizChart";
import {
  KeyTakeawaysCard,
  KeyTakeawaysCardProps,
} from "~/components/KeyTakeawaysCard";
import { LandingPageContainer } from "~/components/LandingPageV2/components/LandingPageContainer";
import { LandingPageContent } from "~/components/LandingPageV2/components/LandingPageContent";
import { GatedContent } from "~/components/Piano";
import { PostEditCTA, PostEditCTAProps } from "~/components/PostEditCTA";
import { Sidecar } from "~/components/Sidecar/Sidecar";
import { SidecarBlock } from "~/components/Sidecar/SidecarBlock";
import { useSidecarProps } from "~/components/Sidecar/useSidecarProps";
import { SocialMediaShareFlyout } from "~/components/SocialMediaShareFlyout";
import { SocialMediaShareFlyoutButton } from "~/components/SocialMediaShareFlyout/SocialMediaShareFlyoutButton";
import { SocialMediaShareMenu } from "~/components/SocialMediaShareMenu";
import { SocialMediaShareMenuButton } from "~/components/SocialMediaShareMenu/SocialMediaShareMenuButton";
import {
  StickyNewsletterFlyout,
  StickyNewsletterProps,
} from "~/components/StickyNewsletter";
import { useStickyNewsletterFlyout } from "~/components/StickyNewsletter/useStickyNewsletterFlyout";
import { Tags, TagsProps } from "~/components/Tags";
import { TrackerNavigationProps } from "~/components/TrackerNavigation";
import { TrackerInlineNavigation } from "~/components/TrackerNavigation/TrackerInlineNavigation";
import { TrackerContentBlock } from "~/components/TrackerPage/TrackerContentBlock";
import {
  TrackerNavigationProvider,
  useTrackerNavigationItemRef,
} from "~/components/TrackerPage/TrackerNavigationProvider";
import {
  TrackerHeader,
  TrackerHeaderProps,
} from "~/components/TrendTrackerHeader";
import { TagToClassNameMap } from "~/lib/shared/html-utils";
import { TrackerContentBlock as ContentBlockType } from "~/types/asgard/Tracker";
import { Body, Headline } from "~/ui-library";
import { getButtonStyles } from "~/ui-library/shared-styles/shared-button-styles";

const sharedHtmlClassNameMap: TagToClassNameMap = new Map([
  ["p", "mb-m last:mb-0"],
]);

const MethodologySection = ({
  methodologyHeadline,
  methodologyNavId,
  methodology,
}: Pick<
  TrackerPageProps,
  | "methodologyNavLabel"
  | "methodologyNavId"
  | "methodologyHeadline"
  | "methodology"
>) => {
  const ref = useTrackerNavigationItemRef(methodologyNavId, {
    threshold: [0, 0.25, 0.5],
  });

  return (
    <div className="flex flex-col" ref={ref}>
      <Headline
        className="mb-s scroll-mt-xxl"
        as="h2"
        id={methodologyNavId}
        level={3}
        mdLevel={2}
      >
        {methodologyHeadline}
      </Headline>
      <Body size="large">
        <BuildHtml
          html={methodology}
          tagToClassNameMap={sharedHtmlClassNameMap}
        />
      </Body>
    </div>
  );
};

const AuthorsSection = ({
  authorBiosProps,
}: Pick<TrackerPageProps, "authorBiosProps">) => (
  <div>
    <div className="flex flex-col gap-m">
      {authorBiosProps!.map(({ ...authorProps }, idx) => (
        <AuthorBio {...authorProps} key={idx} />
      ))}
    </div>
  </div>
);

export type TrackerPageProps = {
  assetDownloadProps: AssetDownloadProps | null;
  authorBiosProps: AuthorBioProps[] | null;
  bylineProps: AuthorBylineSimpleProps;
  contentBlocks: ContentBlockType[];
  headerChartProps: EvervizChartProps | null;
  headerProps: TrackerHeaderProps;
  methodologyHeadline: string;
  methodologyNavId: string;
  methodologyNavLabel: string;
  methodology: string;
  navigationProps: Pick<TrackerNavigationProps, "items">;
  contentPromoProps: Pick<ContentPromoProps, "posts"> | null;
  summary: string;
  tagsProps: TagsProps | null;
  takeawaysProps: KeyTakeawaysCardProps | null;
  toggleUiMenu: boolean;
  shareUrl: string;
  postId: number;
  stickyNewsletterProps: StickyNewsletterProps | null;
  postEditCTAProps: PostEditCTAProps;
  enableSidecar: boolean;
  preview?: boolean;
};

type TrackerActionToolbarProps = {
  onBookmarkClick?: () => void;
  onGetAlertsClick?: () => void;
  onShareClick?: () => void;
  showBookmark: boolean;
  showAlerts: boolean;

  shareTitle: string;
  shareUrl: string;
};

const shareButtonStyles = getButtonStyles({
  size: "extraSmall",
});

const TrackerActionToolbar = ({
  shareTitle,
  shareUrl,
}: TrackerActionToolbarProps) => (
  <div className="flex gap-s">
    <div className="hidden lg:block">
      <SocialMediaShareMenu
        menuButton={
          <SocialMediaShareMenuButton className={shareButtonStyles} />
        }
        shareTitle={shareTitle}
        shareUrl={shareUrl}
      />
    </div>

    <div className="lg:hidden">
      <SocialMediaShareFlyout
        shareTitle={shareTitle}
        shareUrl={shareUrl}
        renderButton={({ onClick }) => (
          <SocialMediaShareFlyoutButton
            className={shareButtonStyles}
            onClick={onClick}
          />
        )}
      />
    </div>
  </div>
);

type BylineSectionProps = Pick<TrackerPageProps, "bylineProps"> &
  TrackerActionToolbarProps;

const BylineSection: React.FC<BylineSectionProps> = ({
  bylineProps,
  onBookmarkClick,
  onGetAlertsClick,
  onShareClick,
  showAlerts,
  showBookmark,
  shareTitle,
  shareUrl,
}) => (
  <div className="flex flex-col gap-m md:flex-row md:items-center md:justify-between md:gap-0">
    <AuthorBylineSimple {...bylineProps} linkAppearance="bold" />

    <TrackerActionToolbar
      onBookmarkClick={onBookmarkClick}
      onGetAlertsClick={onGetAlertsClick}
      onShareClick={onShareClick}
      showAlerts={showAlerts}
      showBookmark={showBookmark}
      shareTitle={shareTitle}
      shareUrl={shareUrl}
    />
  </div>
);

export const TrackerPage: React.FC<TrackerPageProps> = ({
  assetDownloadProps,
  authorBiosProps,
  bylineProps,
  contentBlocks,
  headerChartProps,
  headerProps,
  methodologyHeadline,
  methodologyNavId,
  methodologyNavLabel,
  methodology,
  navigationProps,
  contentPromoProps,
  summary,
  tagsProps,
  takeawaysProps,
  toggleUiMenu,
  postId,
  stickyNewsletterProps,
  postEditCTAProps,
  enableSidecar,
  shareUrl,
  preview,
}) => {
  const { closeFlyout, isShowingFlyout, session, status } =
    useStickyNewsletterFlyout(stickyNewsletterProps?.newsletterSlug);

  const maxContentWidth = enableSidecar ? "memo" : "medium";

  const {
    isLoadingSidecar,
    isValidatingSidecar,
    showDesktopSidecar,
    sidecarBlocks,
    keyTopicsBlock,
    postListBlocks,
    relatedReportBlock,
    wysiwygBlocks,
  } = useSidecarProps({ postId, preview });

  return (
    <TrackerNavigationProvider>
      <PostEditCTA {...postEditCTAProps} />
      <article className="bg-white pb-xl">
        <div className="mx-auto w-full">
          <LandingPageContent
            as="section"
            maxWidth="medium"
            data-testid="header"
            className="mb-l"
          >
            <TrackerHeader {...headerProps} />
          </LandingPageContent>
        </div>
        <div className="w-full">
          <LandingPageContainer
            bottomPadding="none"
            topPadding="none"
            sidecar={
              enableSidecar &&
              showDesktopSidecar && (
                <Sidecar
                  blocks={sidecarBlocks}
                  isLoading={isLoadingSidecar}
                  isValidating={isValidatingSidecar}
                  data-testid="tracker-sidecar"
                />
              )
            }
            className="md:max-w-[1024px]"
          >
            {headerChartProps ? (
              <LandingPageContent
                as="section"
                maxWidth={maxContentWidth}
                className="mb-xl"
                data-testid="header-chart"
              >
                <EvervizChart
                  {...headerChartProps}
                  showHeader={false}
                  key={postId}
                />
              </LandingPageContent>
            ) : null}

            <LandingPageContent
              as="section"
              maxWidth={maxContentWidth}
              data-testid="byline"
            >
              <BylineSection
                bylineProps={bylineProps}
                shareTitle={headerProps.title}
                shareUrl={shareUrl}
                showAlerts={false}
                showBookmark={false}
              />
            </LandingPageContent>

            <LandingPageContent
              as="section"
              maxWidth={maxContentWidth}
              className="mb-l mt-m"
            >
              <Divider />
            </LandingPageContent>

            {toggleUiMenu && navigationProps.items.length ? (
              <LandingPageContent
                as="section"
                className="mb-l mt-m print:hidden"
                maxWidth={maxContentWidth}
              >
                <TrackerInlineNavigation {...navigationProps} />
              </LandingPageContent>
            ) : null}

            {summary ? (
              <LandingPageContent
                as="section"
                maxWidth={maxContentWidth}
                className="mb-xl mt-l"
                data-testid="summary"
              >
                <Body size="large">
                  <BuildHtml
                    html={summary}
                    tagToClassNameMap={sharedHtmlClassNameMap}
                  />
                </Body>
              </LandingPageContent>
            ) : null}
            {takeawaysProps ? (
              <LandingPageContent
                as="section"
                maxWidth={maxContentWidth}
                className="my-xl"
                data-testid="takeaways"
              >
                <KeyTakeawaysCard {...takeawaysProps} />
              </LandingPageContent>
            ) : null}
            {!!(enableSidecar && !showDesktopSidecar && relatedReportBlock) && (
              <LandingPageContent
                as="section"
                maxWidth="memo"
                className="my-l print:hidden"
                data-testid="sidecar-related-report-mobile"
              >
                <Sidecar
                  isLoading={isLoadingSidecar}
                  isValidating={isValidatingSidecar}
                  blocks={[relatedReportBlock]}
                />
              </LandingPageContent>
            )}
            {assetDownloadProps ? (
              <LandingPageContent
                as="section"
                maxWidth={maxContentWidth}
                className="my-xl print:hidden"
                data-testid="asset-download"
              >
                <AssetDownload {...assetDownloadProps} />
              </LandingPageContent>
            ) : null}
            <GatedContent>
              {contentBlocks.map((contentBlock, idx) => (
                <TrackerContentBlock
                  contentBlock={contentBlock}
                  key={`${postId}-${idx}`}
                  maxContentWidth={maxContentWidth}
                />
              ))}
              {methodology ? (
                <LandingPageContent
                  as="section"
                  maxWidth={maxContentWidth}
                  className="my-xl"
                  data-testid="methodology"
                >
                  <MethodologySection
                    methodology={methodology}
                    methodologyHeadline={methodologyHeadline}
                    methodologyNavId={methodologyNavId}
                    methodologyNavLabel={methodologyNavLabel}
                  />
                </LandingPageContent>
              ) : null}

              {!enableSidecar && tagsProps ? (
                <LandingPageContent
                  as="section"
                  maxWidth={maxContentWidth}
                  className="my-xl"
                  data-testid="tags"
                >
                  <Tags {...tagsProps} />
                </LandingPageContent>
              ) : null}

              {!!(enableSidecar && !showDesktopSidecar && keyTopicsBlock) && (
                <LandingPageContent
                  as="section"
                  maxWidth="memo"
                  className="my-l print:hidden"
                  data-testid="sidecar-keytopics-mobile"
                >
                  <SidecarBlock
                    border={false}
                    gutters={false}
                    block={keyTopicsBlock}
                    blockIndex={0}
                  />
                </LandingPageContent>
              )}
            </GatedContent>

            {authorBiosProps ? (
              <LandingPageContent
                as="section"
                maxWidth={maxContentWidth}
                className="mt-xl"
                data-testid="author-bios"
              >
                <AuthorsSection authorBiosProps={authorBiosProps} />
              </LandingPageContent>
            ) : null}

            {!!(
              enableSidecar &&
              !showDesktopSidecar &&
              wysiwygBlocks?.length
            ) &&
              wysiwygBlocks.map((wysiwygBlock, idx) => (
                <LandingPageContent
                  as="section"
                  maxWidth="memo"
                  className="my-l"
                  data-testid="sidecar-wysiwyg-mobile"
                  key={`${wysiwygBlock.acfFcLayout}-${idx}`}
                >
                  <SidecarBlock
                    border={false}
                    gutters={false}
                    block={wysiwygBlock}
                    blockIndex={0}
                  />
                </LandingPageContent>
              ))}

            {!!(
              enableSidecar &&
              !showDesktopSidecar &&
              postListBlocks?.length
            ) &&
              postListBlocks.map((postListBlock, idx) => (
                <LandingPageContent
                  as="section"
                  maxWidth="memo"
                  className="my-l print:hidden"
                  data-testid="sidecar-postlist-mobile"
                  key={`${postListBlock.acfFcLayout}-${idx}`}
                >
                  <SidecarBlock
                    border={false}
                    gutters={false}
                    block={postListBlock}
                    blockIndex={0}
                  />
                </LandingPageContent>
              ))}
          </LandingPageContainer>
        </div>
        <LandingPageContainer topPadding="none" bottomPadding="none">
          <LandingPageContent
            as="section"
            maxWidth="medium"
            className="print:hidden"
          >
            {contentPromoProps ? (
              <>
                <section className="my-xl">
                  <Divider />
                </section>
                <section className="mt-xl" data-id="promos">
                  <ContentPromo
                    headline={
                      <Headline as="h3" level={3} mdLevel={2}>
                        Explore more
                      </Headline>
                    }
                    {...contentPromoProps}
                  />
                </section>
              </>
            ) : null}
          </LandingPageContent>
        </LandingPageContainer>
      </article>
      {stickyNewsletterProps?.iterableListId ? (
        <StickyNewsletterFlyout
          {...stickyNewsletterProps}
          closeFlyout={closeFlyout}
          isShowingFlyout={isShowingFlyout}
          defaultEmail={session?.user.email}
          status={status}
        />
      ) : null}
    </TrackerNavigationProvider>
  );
};
